import { AppEnum } from "@/services/fobizzToolsApi";
import type { ToolDefinition } from "@/types/tools";
import { useI18n } from "vue-i18n";

export function useToolValidation() {
  const { t } = useI18n();

  /**
   * Validate a tool definition has all required properties
   */
  function validateToolDefinition(tool: ToolDefinition): void {
    const required = ["id", "namespace", "name", "clientPath"] as const;
    const missing = required.filter((prop) => !tool[prop]);

    if (missing.length) {
      throw new Error(
        `Tool definition missing required properties: ${missing.join(", ")}`
      );
    }
  }

  /**
   * Get a translated string for a tool name
   */
  function getToolTranslation(key: string): string {
    return t(`apps.${key}.name`);
  }

  /**
   * Standard tool definitions with required properties
   */
  const TOOL_DEFINITIONS: Record<string, ToolDefinition> = {
    assistant: {
      id: AppEnum.TOOLS_AI_ASSISTANT,
      namespace: "ai_assistant",
      name: getToolTranslation("ai_assistant"),
      clientPath: "ai/chats/assistants",
      group: "ai",
      color: "var(--brand-7)",
      icon: "bi-grid-3x3-gap",
      source: "frontend",
    },
    myAssistants: {
      id: AppEnum.TOOLS_AI_ASSISTANT,
      namespace: "ai_my_assistants",
      name: getToolTranslation("ai_my_assistants"),
      clientPath: "ai/chats/assistants/my",
      group: "ai",
      color: "var(--brand-7)",
      icon: "bi-stars",
      source: "frontend",
    },
    miniTool: {
      id: AppEnum.TOOLS_AI_MINI_TOOL,
      namespace: "ai_mini_tool",
      name: getToolTranslation("ai_mini_tool"),
      clientPath: "ai/mini_tools",
      group: "ai",
      color: "var(--brand-7)",
      icon: "bi-lightbulb",
      source: "frontend",
    },
    podcast: {
      id: AppEnum.TOOLS_AI_CONVERTER,
      namespace: "ai_podcast",
      name: getToolTranslation("ai_podcast"),
      clientPath: "ai/converters",
      group: "ai",
      color: "var(--brand-7)",
      icon: "bi-mic",
      queryParams: { outputKind: "podcast" },
      source: "frontend",
    },
    audio: {
      id: AppEnum.TOOLS_AI_CONVERTER,
      namespace: "ai_audio",
      name: getToolTranslation("ai_audio"),
      clientPath: "ai/converters",
      group: "ai",
      color: "var(--brand-7)",
      icon: "bi-soundwave",
      queryParams: { outputKind: "audio" },
      source: "frontend",
    },
    text: {
      id: AppEnum.TOOLS_AI_CONVERTER,
      namespace: "ai_text",
      name: getToolTranslation("ai_text"),
      clientPath: "ai/converters",
      group: "ai",
      color: "var(--brand-7)",
      icon: "bi-file-earmark-font",
      queryParams: { outputKind: "text" },
      source: "frontend",
    },
    lecture: {
      id: AppEnum.TOOLS_AI_CONVERTER,
      namespace: "ai_lecture",
      name: getToolTranslation("ai_lecture"),
      clientPath: "ai/converters",
      group: "ai",
      color: "var(--brand-7)",
      icon: "bi-book",
      queryParams: { outputKind: "lecture" },
      source: "frontend",
    },
    transcript: {
      id: AppEnum.TOOLS_AI_CONVERTER,
      namespace: "ai_transcript",
      name: getToolTranslation("ai_transcript"),
      clientPath: "ai/converters",
      group: "ai",
      color: "var(--brand-7)",
      icon: "bi-vector-pen",
      queryParams: { outputKind: "transcript" },
      source: "frontend",
    },
  };

  return {
    validateToolDefinition,
    getToolTranslation,
    TOOL_DEFINITIONS,
  };
}
